import { ref, watch } from 'vue'
import type { FrameMenuItem } from 'app/types'
import type { ServiceInitArgs } from 'app/services/base'
import { useMindMap, viewerKit } from 'compositions/mindmap'
import templateBuilder from 'client/pages/app/fragments/compositions/ui/context-menu/template-builder'
import debounce from 'utils/debounce'
import { trackNewVersionWorks } from 'client/library/tracker'
import { useGlobalConfig } from '../../composables/use-global-config'
import { buildEmbedTopicMenu } from './embed-editor-topic-menu'
import { buildMapMenu } from './embed-editor-map-menu'
import { buildLabelsMenu } from './embed-editor-labels-menu'
import { buildNoteMenu } from './embed-editor-note-menu'
import { buildRelationshipAndBoundaryMenu } from './embed-editor-relationship-and-boundary-menu'

export const useContextMenu = (args: { services: ServiceInitArgs; popMenu?: (point: { x: number; y: number }) => void | Promise<void> }) => {
  const { services, popMenu } = args
  const menuItems = ref<FrameMenuItem[] | null>(null)
  const popupMenu = popMenu ?? (() => {})
  const { embedEditorPreset } = useGlobalConfig()

  const { installContextMenuHandler } = useMindMap()

  const mindMapContextMenuHandler = event => {
    const point = { x: event.contextMenuRequest.locationInDOMElement.x, y: event.contextMenuRequest.locationInDOMElement.y }

    if (event.type === 'map') {
      return waitSelectionEmpty().then(() => {
        const pointerTarget = event.viewerState.pointerTarget
        if (pointerTarget !== null) return

        menuItems.value = buildMapMenu(services, event.contextMenuRequest.locationInContent)
        trackNewVersionWorks.editorRightClickOnEmpty('editorRightClick')
        popupMenu(point)
      })
    }

    if (event.type === 'topic') {
      return waitSelectionAndActionStatesUpdate().then(() => {
        const currentTargetTopicType = templateBuilder.getCurrentTargetTopicType(
          event.viewerState.pointerTarget as viewerKit.TopicEditTarget,
          event.viewerState.selection,
          event.sheet,
        )
        menuItems.value = buildEmbedTopicMenu(services, { currentTargetTopicType })
        trackNewVersionWorks.topicRightClick('topicRightClick')
        popupMenu(point)
      })
    }

    if (event.type === 'relationship' || event.type === 'boundary') {
      return waitSelectionAndActionStatesUpdate().then(() => {
        menuItems.value = buildRelationshipAndBoundaryMenu(services)
        popupMenu(point)
      })
    }

    if (event.type === 'label') {
      if (!embedEditorPreset.enableNoteLabel) {
        return
      }
      return waitSelectionAndActionStatesUpdate().then(() => {
        const labelsForDelete = templateBuilder.getResultLabelsForDelete(
          event.viewerState.pointerTarget as viewerKit.LabelEditTarget,
          event.viewerState.selection,
          event.sheet,
        )
        menuItems.value = buildLabelsMenu(services, { labelsForDelete })
        popupMenu(point)
      })
    }

    if (event.type === 'infoButton') {
      if (event.viewerState.pointerTarget.kind !== 'info-button') return
      if (!embedEditorPreset.enableNoteLabel) {
        return
      }
      return waitSelectionAndActionStatesUpdate().then(() => {
        const infoButtonType = templateBuilder.getInfoButtonType(
          event.viewerState.pointerTarget as viewerKit.InfoButtonEditTarget,
          event.viewerState.selection,
        )

        if (infoButtonType === viewerKit.InfoItemType.Note) {
          menuItems.value = buildNoteMenu(services)
          popupMenu(point)
        }
      })
    }

    if (event.type === 'notes') {
      if (!embedEditorPreset.enableNoteLabel) {
        return
      }
      return waitSelectionAndActionStatesUpdate().then(() => {
        const pointerTarget = event.viewerState.pointerTarget
        if (pointerTarget?.kind !== 'notes') return

        menuItems.value = buildNoteMenu(services, { isHideDisplayNote: true })
        popupMenu(point)
      })
    }
  }

  installContextMenuHandler(mindMapContextMenuHandler)

  return {
    menuItems,
    popupMenu,
  }
}

async function waitSelectionEmpty() {
  return Promise.race([
    new Promise(resolve => {
      const { selection } = useMindMap()
      if (selection.value.type === 'none') {
        resolve(true)
        return
      }

      const unwatch = watch(selection, selectionValue => {
        if (selectionValue.type === 'none') {
          unwatch?.()
          resolve(true)
        }
      })
    }),
    new Promise(resolve => setTimeout(resolve, 300)),
  ])
}

async function waitSelectionAndActionStatesUpdate() {
  return Promise.race([
    new Promise(resolve => {
      const { selection, actionStates } = useMindMap()

      const unwatch = watch(
        () => [selection.value, actionStates.value],
        debounce(() => {
          unwatch?.()
          resolve(true)
        }, 50),
        { immediate: true },
      )
    }),
    new Promise(resolve => setTimeout(resolve, 300)),
  ])
}
